import {
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  LabelList,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import { Download, Maximize2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAppStore } from "@/stores";
import type {
  RevenueIntelligenceChatChartSpec,
  RevenueIntelligenceChatDataset,
  RevenueIntelligenceChatDatasetRow,
} from "./RevenueIntelligenceChatContext";
import {
  exportRevenueIntelligenceChart,
  getRevenueIntelligenceExportPeriodLabel,
} from "../lib/revenue-intelligence-chart-export";
import { getRevenueIntelligenceScatterEncodingLabels } from "./revenue-intelligence-chat-chart-semantics";

const chartColors = ["#1D819A", "#231942", "#9F86C0", "#E0B1CB"];
type ChartLanguage = "en" | "sv";

const swedishFieldLabels: Record<string, string> = {
  "Deal": "Affär",
  "Deal value": "Affärsvärde",
  "Forecast contribution": "Prognosbidrag",
  "Month": "Månad",
  "Owner": "Ägare",
  "Pipeline Risk score": "Pipeline Risk-poäng",
  "Target": "Mål",
};

function getFieldLabel(
  dataset: RevenueIntelligenceChatDataset,
  field: string,
  language: ChartLanguage = "en",
) {
  const label = [...dataset.dimensions, ...dataset.measures]
    .find(item => item.field === field)?.label ?? field;
  return language === "sv" ? swedishFieldLabels[label] ?? label : label;
}

function formatChartFieldValue(
  dataset: RevenueIntelligenceChatDataset,
  fieldName: string,
  value: string | number | null,
  currencyCode = "SEK",
) {
  if (typeof value !== "number") return String(value ?? "—");
  const field = [...dataset.dimensions, ...dataset.measures]
    .find(item => item.field === fieldName);
  if (field?.type === "percentage") return `${value}%`;
  const formatted = new Intl.NumberFormat("en-GB", {
    maximumFractionDigits: 1,
    notation: field?.type === "currency" ? "compact" : "standard",
  }).format(value);
  return field?.type === "currency" ? `${formatted} ${currencyCode}` : formatted;
}

function RecordScatterTooltip({
  active,
  chart,
  dataset,
  payload,
  currencyCode,
  language,
}: {
  active?: boolean;
  chart: RevenueIntelligenceChatChartSpec;
  dataset: RevenueIntelligenceChatDataset;
  payload?: Array<{ payload?: RevenueIntelligenceChatDatasetRow }>;
  currencyCode?: string;
  language: ChartLanguage;
}) {
  const row = payload?.[0]?.payload;
  if (!active || !row) return null;
  const labelField = chart.groupBy
    ?? dataset.dimensions.find(field => field.type === "category")?.field;
  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow-sm">
      {labelField && (
        <p className="mb-1 font-medium text-[#231942]">
          {String(row[labelField] ?? "Not specified")}
        </p>
      )}
      {[chart.xField, ...chart.yFields].map(field => (
        <p key={field} className="text-gray-600">
          {getFieldLabel(dataset, field, language)}: {formatChartFieldValue(dataset, field, row[field] ?? null, currencyCode)}
        </p>
      ))}
    </div>
  );
}

const sharedAxisProps = {
  axisLine: false,
  tickLine: false,
  tick: { fill: "#6B7280", fontSize: 10 },
} as const;

export function RevenueIntelligenceChatChartVisual({
  chart,
  className = "h-56 px-2 py-3",
  containerRef,
  dataset,
  language = "en",
}: {
  chart: RevenueIntelligenceChatChartSpec;
  className?: string;
  containerRef?: RefObject<HTMLDivElement>;
  dataset: RevenueIntelligenceChatDataset;
  language?: ChartLanguage;
}) {
  const currencyCode = useAppStore(state => state.companyCurrency?.code ?? "SEK");
  const labelField = chart.groupBy
    ?? dataset.dimensions.find(field => field.type === "category")?.field;
  const xLabel = `${getFieldLabel(dataset, chart.xField, language)}${dataset.measures.find(field => field.field === chart.xField)?.type === "currency" ? ` (${currencyCode})` : ""}`;
  const yField = chart.yFields[0];
  const yLabel = yField
    ? `${getFieldLabel(dataset, yField, language)}${dataset.measures.find(field => field.field === yField)?.type === "currency" ? ` (${currencyCode})` : ""}`
    : "";
  const scatterEncoding = getRevenueIntelligenceScatterEncodingLabels(
    chart,
    dataset,
    language,
    field => getFieldLabel(dataset, field, language),
  );
  return (
    <div ref={containerRef} className={`${className} flex flex-col`}>
      {scatterEncoding.length > 0 && (
        <div className="mb-1 flex flex-wrap justify-center gap-x-3 gap-y-1 text-[10px] text-gray-500">
          {scatterEncoding.map(item => <span key={item}>{item}</span>)}
        </div>
      )}
      <div className="min-h-0 flex-1">
        <ResponsiveContainer width="100%" height="100%">
        {chart.type === "bar" ? (
          <BarChart data={dataset.rows} margin={{ bottom: 12, left: 0, right: 8, top: 8 }}>
            <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey={chart.xField} {...sharedAxisProps} />
            <YAxis {...sharedAxisProps} width={42} />
            <Tooltip />
            <Legend />
            {chart.yFields.map((field, index) => (
              <Bar
                key={field}
                dataKey={field}
                fill={chartColors[index % chartColors.length]}
                name={getFieldLabel(dataset, field, language)}
                radius={[4, 4, 0, 0]}
              />
            ))}
          </BarChart>
        ) : chart.type === "scatter" ? (
          <ScatterChart margin={{ bottom: 18, left: 0, right: 16, top: 8 }}>
            <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" />
            <XAxis
              dataKey={chart.xField}
              name={getFieldLabel(dataset, chart.xField, language)}
              tickFormatter={value => formatChartFieldValue(dataset, chart.xField, value, currencyCode)}
              type="number"
              label={{ value: xLabel, position: "insideBottom", offset: -12, fill: "#6B7280", fontSize: 10 }}
              {...sharedAxisProps}
            />
            <YAxis
              dataKey={chart.yFields[0]}
              name={getFieldLabel(dataset, chart.yFields[0], language)}
              tickFormatter={value => formatChartFieldValue(dataset, chart.yFields[0], value, currencyCode)}
              type="number"
              label={{ value: yLabel, angle: -90, position: "insideLeft", fill: "#6B7280", fontSize: 10 }}
              {...sharedAxisProps}
              width={38}
            />
            {chart.yFields[1] && (
              <ZAxis dataKey={chart.yFields[1]} range={[80, 360]} type="number" />
            )}
            <Tooltip
              content={props => (
                <RecordScatterTooltip
                  active={props.active}
                  chart={chart}
                  dataset={dataset}
                  currencyCode={currencyCode}
                  language={language}
                  payload={props.payload as Array<{ payload?: RevenueIntelligenceChatDatasetRow }>}
                />
              )}
              cursor={{ strokeDasharray: "3 3" }}
            />
            <Scatter
              data={dataset.rows}
              fill="#5E548E"
              name={dataset.label}
            >
              {labelField && dataset.rows.length <= 5 && (
                <LabelList
                  dataKey={labelField}
                  fill="#5E548E"
                  fontSize={9}
                  formatter={(value: unknown) => {
                    const label = String(value ?? "");
                    const maximumLength = dataset.rows.length <= 3 ? 30 : 18;
                    return label.length > maximumLength
                      ? `${label.slice(0, maximumLength - 1)}…`
                      : label;
                  }}
                  position="top"
                />
              )}
            </Scatter>
          </ScatterChart>
        ) : (
          <LineChart data={dataset.rows} margin={{ bottom: 12, left: 0, right: 8, top: 8 }}>
            <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey={chart.xField} {...sharedAxisProps} />
            <YAxis {...sharedAxisProps} width={42} />
            <Tooltip />
            <Legend />
            {chart.yFields.map((field, index) => (
              <Line
                key={field}
                dataKey={field}
                dot={false}
                name={getFieldLabel(dataset, field, language)}
                stroke={chartColors[index % chartColors.length]}
                strokeWidth={2}
                type="monotone"
              />
            ))}
          </LineChart>
        )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function RevenueIntelligenceChatChart({
  autoExpand = false,
  chart,
  conclusion,
  dataset,
  language = "en",
  onAutoExpanded,
  onSave,
  period,
}: {
  autoExpand?: boolean;
  chart: RevenueIntelligenceChatChartSpec;
  conclusion?: string | null;
  dataset: RevenueIntelligenceChatDataset;
  language?: ChartLanguage;
  onAutoExpanded?: () => void;
  onSave: () => Promise<boolean>;
  period?: string | null;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState<"png" | "svg" | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [expanded, setExpanded] = useState(autoExpand);
  useEffect(() => {
    if (autoExpand) onAutoExpanded?.();
  }, [autoExpand, onAutoExpanded]);
  const dataAsOf = useMemo(
    () => new Intl.DateTimeFormat(language === "sv" ? "sv-SE" : "en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date()),
    [language],
  );
  const rawPeriodLabel = getRevenueIntelligenceExportPeriodLabel(period);
  const periodLabel = language === "sv"
    ? ({
        "Next quarter": "Nästa kvartal",
        "This quarter": "Detta kvartal",
        "Previous quarter": "Föregående kvartal",
        "This year": "I år",
        "Next 6 months": "Nästa 6 månader",
      }[rawPeriodLabel ?? ""] ?? rawPeriodLabel)
    : rawPeriodLabel;
  const dataAsOfLabel = language === "sv" ? `Data per ${dataAsOf}` : `Data as of ${dataAsOf}`;
  const exportChart = async (format: "png" | "svg") => {
    const svg = containerRef.current?.querySelector("svg");
    if (!svg) return;
    setExporting(format);
    try {
      await exportRevenueIntelligenceChart({
        chart: svg,
        conclusion: chart.showConclusion ? conclusion : null,
        dataAsOf,
        format,
        period,
        source: dataset.label,
        title: chart.title,
        language,
      });
    } finally {
      setExporting(null);
    }
  };

  const actions = (large = false) => (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        type="button"
        size="sm"
        className={large
          ? "h-9 gap-2 bg-[#231942] px-4 text-xs text-white hover:bg-[#35265f]"
          : "h-8 gap-1.5 bg-[#231942] px-3 text-[11px] text-white hover:bg-[#35265f]"}
        disabled={saving || saved}
        onClick={async () => {
          setSaving(true);
          try {
            setSaved(await onSave());
          } finally {
            setSaving(false);
          }
        }}
      >
        <Save className="h-3.5 w-3.5" />
        {saving
          ? language === "sv" ? "Sparar…" : "Saving…"
          : saved
            ? language === "sv" ? "Sparad i Agents" : "Saved to Agents"
            : language === "sv" ? "Spara i Agents" : "Save to Agents"}
      </Button>
      <div className="flex items-center rounded-md bg-[#5E548E]/8 p-0.5">
        {(["png", "svg"] as const).map(format => (
          <Button
            key={format}
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 px-2.5 text-[10px] font-medium text-[#5E548E] hover:bg-white hover:text-[#231942]"
            disabled={exporting !== null}
            onClick={() => exportChart(format)}
          >
            <Download className="h-3 w-3" />
            {exporting === format
              ? language === "sv" ? "Förbereder…" : "Preparing…"
              : format.toUpperCase()}
          </Button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="mt-3 overflow-hidden rounded-[10px] border border-[#5E548E]/15 bg-white">
      <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-3 py-2.5">
        <div className="min-w-0">
          <p className="text-xs font-medium text-[#231942]">{chart.title}</p>
          <p className="mt-0.5 text-[10px] text-gray-500">
            {[periodLabel, dataAsOfLabel].filter(Boolean).join(" · ")}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="shrink-0 text-[#5E548E]"
          onClick={() => setExpanded(true)}
          title="Expand chart"
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      <RevenueIntelligenceChatChartVisual
            chart={chart}
            containerRef={containerRef}
            dataset={dataset}
            language={language}
      />
      <div className="border-t border-gray-100 px-3 py-2.5">
        {actions()}
      </div>
      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent className="max-w-5xl p-0">
          <DialogHeader className="border-b px-6 py-4 text-left">
            <DialogTitle className="text-lg text-[#231942]">{chart.title}</DialogTitle>
            <DialogDescription>
              {[periodLabel, dataAsOfLabel].filter(Boolean).join(" · ")}
            </DialogDescription>
          </DialogHeader>
          <RevenueIntelligenceChatChartVisual
            chart={chart}
            className="h-[min(64vh,620px)] px-8 py-6"
            dataset={dataset}
            language={language}
          />
          <div className="flex items-center justify-between border-t px-6 py-4">
            <p className="text-[11px] text-gray-500">
              {language === "sv" ? "Källa" : "Source"}: {dataset.label}
            </p>
            {actions(true)}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
